import React, { useState } from 'react';
import { Card, Table, Button, Typography, Space, Input, Form, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { rejectionService, type RejectionLogDTO } from '@/services/rejection.service';

const { Title, Text } = Typography;

const pageSize = 50;

const RejectionRegisterPage: React.FC = () => {
  const [form] = Form.useForm();
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState<{
    from?: string;
    to?: string;
    document_type?: string;
    module?: string;
    reason_code?: string;
  }>({});

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['rejection-register', filters, offset],
    queryFn: () =>
      rejectionService.register({
        ...filters,
        limit: pageSize,
        offset,
      }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  const onSearch = () => {
    const v = form.getFieldsValue();
    const range = v.period as [Dayjs, Dayjs] | undefined;
    setOffset(0);
    setFilters({
      from: range?.[0] ? dayjs(range[0]).format('YYYY-MM-DD') : undefined,
      to: range?.[1] ? dayjs(range[1]).format('YYYY-MM-DD') : undefined,
      document_type: v.document_type?.trim() || undefined,
      module: v.module?.trim() || undefined,
      reason_code: v.reason_code?.trim() || undefined,
    });
  };

  const columns: ColumnsType<RejectionLogDTO> = [
    {
      title: 'Rejected at',
      dataIndex: 'rejected_at',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    { title: 'Module', dataIndex: 'module', width: 120, ellipsis: true },
    { title: 'Document type', dataIndex: 'document_type', width: 160, ellipsis: true },
    { title: 'Document #', dataIndex: 'document_number', width: 140, ellipsis: true },
    { title: 'Reason code', dataIndex: 'rejection_reason_code', width: 120 },
    { title: 'Reason', dataIndex: 'rejection_reason_text', ellipsis: true },
    { title: 'Remarks', dataIndex: 'remarks', ellipsis: true },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            Rejection register
          </Title>
          <Text type="secondary">Immutable rejection audit trail (company scope).</Text>
        </div>
        <Card>
          <Form form={form} layout="inline" style={{ marginBottom: 16, rowGap: 8 }} onFinish={onSearch}>
            <Form.Item name="period" label="Date range">
              <DatePicker.RangePicker allowClear />
            </Form.Item>
            <Form.Item name="document_type" label="Document type">
              <Input placeholder="entity_type" style={{ width: 160 }} allowClear />
            </Form.Item>
            <Form.Item name="module" label="Module">
              <Input style={{ width: 120 }} allowClear />
            </Form.Item>
            <Form.Item name="reason_code" label="Reason code">
              <Input style={{ width: 120 }} allowClear />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Apply
              </Button>
            </Form.Item>
            <Form.Item>
              <Button onClick={() => void refetch()} loading={isFetching}>
                Refresh
              </Button>
            </Form.Item>
          </Form>
          <Table<RejectionLogDTO>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={rows}
            scroll={{ x: 1100 }}
            pagination={{
              current: Math.floor(offset / pageSize) + 1,
              pageSize,
              total,
              showSizeChanger: false,
              onChange: (page) => setOffset((page - 1) * pageSize),
            }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default RejectionRegisterPage;
